import React, { useState, useEffect } from 'react'
import ReactSelect from 'react-select'
import s from '../../styles.module.css'

const FEET_OPTIONS = Array.from({ length: 8 }, (_, i) => ({
  value: i,
  label: String(i),
}))

const INCH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: i,
  label: String(i),
}))

const getFeet = (totalInches: number | undefined): number | undefined => {
  if (totalInches === undefined || totalInches === null) return undefined
  return Math.floor(totalInches / 12)
}

const getInches = (totalInches: number | undefined): number | undefined => {
  if (totalInches === undefined || totalInches === null) return undefined
  return totalInches % 12
}

interface SurveyHeightPickerProps {
  inches?: number
  onChange: (totalInches: number) => void
}

const SurveyHeightPicker: React.FC<SurveyHeightPickerProps> = ({
  inches: totalInches,
  onChange,
}) => {
  const [feet, setFeet] = useState<number | undefined>(getFeet(totalInches))
  const [inches, setInches] = useState<number | undefined>(
    getInches(totalInches)
  )

  useEffect(() => {
    setFeet(getFeet(totalInches))
    setInches(getInches(totalInches))
  }, [totalInches])

  const handleFeetChange = (
    selectedOption: { value: number; label: string } | null
  ) => {
    const newFeet = selectedOption ? selectedOption.value : 0
    setFeet(newFeet)
    const total = newFeet * 12 + (inches || 0)
    onChange(total)
  }

  const handleInchesChange = (
    selectedOption: { value: number; label: string } | null
  ) => {
    const newInches = selectedOption ? selectedOption.value : 0
    setInches(newInches)
    const total = (feet || 0) * 12 + newInches
    onChange(total)
  }

  const selectStyles = {
    control: (base: any, props: any) => ({
      ...base,
      background: '#fff',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      minHeight: '44px',
      boxShadow: props.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
      borderColor: props.isFocused ? '#3b82f6' : '#e2e8f0',
      ':hover': {
        borderColor: props.isFocused ? '#3b82f6' : '#cbd5e0',
      },
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: '0.5rem 0.75rem',
    }),
    input: (base: any) => ({
      ...base,
      margin: 0,
      padding: 0,
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: '#6b7280',
      ':hover': {
        color: '#374151',
      },
    }),
    option: (styles: any, { isFocused, isSelected }: any) => ({
      ...styles,
      backgroundColor: isSelected ? '#3b82f6' : isFocused ? '#eff6ff' : '#fff',
      color: isSelected ? '#fff' : '#2d3748',
      cursor: 'pointer',
      ':active': {
        ...styles[':active'],
        backgroundColor: isSelected ? '#2563eb' : '#dbeafe',
      },
    }),
    menu: (base: any) => ({
      ...base,
      borderRadius: '0.5rem',
      border: '1px solid #e2e8f0',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    }),
    placeholder: (base: any) => ({
      ...base,
      color: '#9ca3af',
    }),
  }

  return (
    <div className={s.heightPickerContainer}>
      <div className={s.heightPickerInputs}>
        <div className={s.heightPickerSelect}>
          <ReactSelect
            options={FEET_OPTIONS}
            value={
              feet !== undefined
                ? FEET_OPTIONS.find(opt => opt.value === feet)
                : null
            }
            onChange={handleFeetChange}
            placeholder="ft"
            isClearable={false}
            isSearchable={false}
            styles={selectStyles}
          />
        </div>
        <div className={s.heightPickerSelect}>
          <ReactSelect
            options={INCH_OPTIONS}
            value={
              inches !== undefined
                ? INCH_OPTIONS.find(opt => opt.value === inches)
                : null
            }
            onChange={handleInchesChange}
            placeholder="in"
            isClearable={false}
            isSearchable={false}
            styles={selectStyles}
          />
        </div>
      </div>
    </div>
  )
}

export default SurveyHeightPicker
